import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Cinematic count-up animation
const countUpAnimation = keyframes`
  0% {
    transform: translateY(10px) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: translateY(-2px) scale(1.1);
    opacity: 1;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

// Pulse effect for changes
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.15);
    filter: brightness(1.3);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

const AnimatedCounterWrapper = styled.span`
  display: inline-block;
  min-width: 16px;
  text-align: center;
  transition: color 0.2s ease;
  
  &.animating {
    animation: ${countUpAnimation} 0.4s ease-out;
    color: #4CAF50; /* Subtle green for positive changes */
  }
  
  &.pulsing {
    animation: ${pulseAnimation} 0.3s ease-in-out;
  }
  
  &.decreasing {
    color: #FF6B6B; /* Subtle red flash for decreases */
    transition: color 0.2s ease;
  }
`;

const AnimatedCounter = ({ count, className = '' }) => {
  const [displayCount, setDisplayCount] = useState(count);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [isDecreasing, setIsDecreasing] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  useEffect(() => {
    if (count !== prevCount) {
      // Trigger pulse animation for any change
      setIsPulsing(true);
      
      // For count increases, animate the count up
      if (count > prevCount) {
        setIsAnimating(true);
        
        // Animate count-up over time for cinematic effect
        const diff = count - prevCount;
        const duration = Math.min(300, diff * 50); // Max 300ms
        const steps = Math.min(diff, 10); // Max 10 steps
        const stepDuration = duration / steps;
        
        let currentStep = 0;
        const interval = setInterval(() => {
          currentStep++;
          const progress = currentStep / steps;
          const newCount = Math.round(prevCount + (diff * progress));
          setDisplayCount(newCount);
          
          if (currentStep >= steps) {
            clearInterval(interval);
            setDisplayCount(count);
            setTimeout(() => setIsAnimating(false), 100);
          }
        }, stepDuration);
        
        return () => clearInterval(interval);
      } else {
        // For decreases, show decreasing effect and update immediately
        setIsDecreasing(true);
        setDisplayCount(count);
        setTimeout(() => setIsDecreasing(false), 300);
      }
      
      setPrevCount(count);
      
      // Clear pulse animation
      setTimeout(() => setIsPulsing(false), 300);
    }
  }, [count, prevCount]);

  // Handle initial count set
  useEffect(() => {
    if (prevCount !== count && prevCount === 0) {
      setDisplayCount(count);
      setPrevCount(count);
    }
  }, [count, prevCount]);

  return (
    <AnimatedCounterWrapper 
      className={`${className} ${isAnimating ? 'animating' : ''} ${isPulsing ? 'pulsing' : ''} ${isDecreasing ? 'decreasing' : ''}`}
    >
      {displayCount}
    </AnimatedCounterWrapper>
  );
};

export default AnimatedCounter;